import React from "react";
import { faro } from "@grafana/faro-web-sdk";

/**
 * Isolates one timeline slide.
 *
 * The app-level ErrorBoundary wraps the whole frontend, so a single template
 * throwing during render replaced the entire screen with the fallback and the
 * loop stopped dead. On air that reads as the graphics breaking.
 *
 * This boundary sits around each slide instead. A template that throws renders
 * as nothing, the slide it was in stays empty for its turn, and the timeline
 * keeps advancing to the next one.
 *
 * `resetKey` is whatever identifies the slide's current content - when it
 * changes we have new data, so we clear the error and let the template try
 * again rather than blanking that slot for the rest of the broadcast.
 */
class SlideErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { errored: false, lastKey: props.resetKey };
  }

  static getDerivedStateFromError() {
    return { errored: true };
  }

  static getDerivedStateFromProps(props, state) {
    // Healthy: keep track of which slide we are showing, so that when one does
    // throw we already know which key failed.
    if (!state.errored) {
      return state.lastKey === props.resetKey ? null : { lastKey: props.resetKey };
    }

    // Errored: recover as soon as the slide's content actually changes.
    if (props.resetKey !== state.lastKey) {
      return { errored: false, lastKey: props.resetKey };
    }

    return null;
  }

  componentDidCatch(error, errorInfo) {
    // Keep the failure visible in monitoring even though the viewer sees nothing.
    if (faro?.api) {
      faro.api.pushError(error, {
        context: {
          message: "Timeline slide error boundary",
          slide: String(this.props.name || "unknown"),
          resetKey: String(this.props.resetKey || ""),
          componentStack: errorInfo?.componentStack,
        },
      });
    }

    // eslint-disable-next-line no-console
    console.error(
      `Timeline slide "${this.props.name || "unknown"}" failed to render; skipping it.`,
      error
    );
  }

  render() {
    if (this.state.errored) {
      // Nothing rather than a fallback graphic - an empty slot is far less
      // noticeable on air than an error card, and the next slide is seconds away.
      return null;
    }

    return this.props.children;
  }
}

export default SlideErrorBoundary;
